import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import WysiwygEditor from "../../Components/Custom/DashlyDashboard/WysiwygEditor/WysiwygEditor";
import { DashlyTextInput } from "../../Components/inputs";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import {
    IMediaAssetModel,
    MEDIA_TYPE,
    MediaAssetService,
    MediaType,
    checkUploadableFiles,
    isImageAttachment,
    isVideoAttachment,
    looksLikeVideoFile,
    mediaThumbnailUrl,
    photoDisplayUrl,
} from "../../Services/MediaAssetService";
import { IKnowledgeArticleSectionModel, MOVE_DIRECTION, MoveDirection, SECTION_TYPE } from "../../Services/KnowledgeArticleSectionService";
import DeferredVideoPlayer from "./DeferredVideoPlayer";
import MediaDropZone from "./MediaDropZone";
import MediaUploadPreviewDialog from "./MediaUploadPreviewDialog";
import SelectExistingMediaDialog from "./SelectExistingMediaDialog";
import "./ArticleSectionEditor.scss";

export type ArticleSectionChanges = Partial<Pick<IKnowledgeArticleSectionModel, "title" | "content" | "showTitle">>;

interface ArticleSectionEditorProps {
    /** The section as the article editor currently holds it. That page owns (and autosaves) the values;
     *  this component only reports what the author changes. */
    section: IKnowledgeArticleSectionModel;
    /** Every media asset of the article, signed URLs included. */
    mediaAssets: IMediaAssetModel[];
    /** Already-uploaded PHOTO/VIDEO assets elsewhere on the article this section could reuse instead of
     *  re-uploading — filtered further below to exclude this section's own current items. */
    existingMediaAssets: IMediaAssetModel[];
    knowledgeArticleUuid: string;
    isFirst: boolean;
    isLast: boolean;
    onChange: (changes: ArticleSectionChanges) => void;
    onMove: (direction: MoveDirection) => void;
    onDelete: () => void;
    onMediaChanged: () => void;
}

const SECTION_TYPE_ICON: Record<string, string> = {
    [SECTION_TYPE.IMAGE]: "pi-image",
    [SECTION_TYPE.VIDEO]: "pi-video",
    [SECTION_TYPE.SLIDESHOW]: "pi-images",
    [SECTION_TYPE.TEXT]: "pi-align-left",
};

/** One row in the article editor's section list — shared chrome (title/type/move/delete) plus a
 *  type-specific body. Title, text and "show title" are saved by the article editor as the author works,
 *  independently of the article's own "Save" button; media uploads are saved right here, immediately. */
const ArticleSectionEditor: React.FC<ArticleSectionEditorProps> = ({
    section,
    mediaAssets,
    existingMediaAssets,
    knowledgeArticleUuid,
    isFirst,
    isLast,
    onChange,
    onMove,
    onDelete,
    onMediaChanged,
}) => {
    const { t } = useTranslation();
    const mediaAssetService = useMemo(() => new MediaAssetService(), []);
    const [pickerVisible, setPickerVisible] = useState(false);
    // The title is only handed on when the author leaves the field: a section cannot have an empty title,
    // and half a word is not worth a save.
    const [titleDraft, setTitleDraft] = useState(section.title);
    const [uploading, setUploading] = useState(false);
    const [pendingUpload, setPendingUpload] = useState<{ files: File[]; mediaType?: MediaType } | null>(null);

    const assets = useMemo(() => mediaAssets.filter((asset) => asset.sectionUuid === section.uuid), [mediaAssets, section.uuid]);
    const showTitle = section.showTitle !== false;

    useEffect(() => {
        setTitleDraft(section.title);
    }, [section.title]);

    const handleTitleBlur = () => {
        const trimmed = titleDraft.trim();
        if (trimmed && trimmed !== section.title) {
            onChange({ title: trimmed });
        } else {
            setTitleDraft(section.title);
        }
    };

    const uploadFiles = async (files: File[], mediaTypeOverride?: MediaType) => {
        setUploading(true);
        try {
            for (const file of files) {
                const mediaType = mediaTypeOverride ?? (looksLikeVideoFile(file) ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO);
                await mediaAssetService.uploadMediaAsset(knowledgeArticleUuid, mediaType, file, section.uuid);
            }
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t));
        } finally {
            setUploading(false);
            // Also after a failure halfway: what did get uploaded before it has to show up.
            onMediaChanged();
        }
    };

    /** Files chosen via MediaDropZone don't upload immediately — they first go through
     *  MediaUploadPreviewDialog so the user can see (and, for a photo, crop/resize) them before anything
     *  is saved; the actual upload only happens once that dialog is confirmed. A file over its size or
     *  duration cap does not even get that far. */
    const handleFilesSelected = async (files: File[], mediaTypeOverride?: MediaType) => {
        const { accepted, violations } = await checkUploadableFiles(files);
        violations.forEach((violation) => toast.error(t(violation) as string));
        if (accepted.length > 0) {
            setPendingUpload({ files: accepted, mediaType: mediaTypeOverride });
        }
    };

    const handlePreviewConfirm = (finalFiles: File[]) => {
        const mediaType = pendingUpload?.mediaType;
        setPendingUpload(null);
        uploadFiles(finalFiles, mediaType);
    };

    const handlePreviewCancel = () => setPendingUpload(null);

    const handleDeleteAsset = async (assetUuid: string) => {
        try {
            await mediaAssetService.deleteMediaAsset(assetUuid);
            onMediaChanged();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaDeleteError", t));
        }
    };

    const handleSelectExisting = async (asset: IMediaAssetModel) => {
        try {
            await mediaAssetService.assignToSection(asset.uuid, section.uuid);
            setPickerVisible(false);
            onMediaChanged();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.saveError", t));
        }
    };

    const renderBody = () => {
        if (section.sectionType === SECTION_TYPE.TEXT) {
            return (
                <WysiwygEditor
                    value={section.content ?? ""}
                    editMode
                    onChange={(content) => onChange({ content })}
                    knowledgeArticleUuid={knowledgeArticleUuid}
                    sectionUuid={section.uuid}
                    mediaAssets={mediaAssets}
                    onImageUploaded={onMediaChanged}
                />
            );
        }

        if (section.sectionType === SECTION_TYPE.SLIDESHOW) {
            return (
                <div className="article-section-editor__slideshow">
                    {assets.length > 0 && (
                        <div className="article-section-editor__slideshow-items">
                            {assets.map((asset) => (
                                <div key={asset.uuid} className="article-section-editor__slideshow-item">
                                    {mediaThumbnailUrl(asset) ? (
                                        <img src={mediaThumbnailUrl(asset)} alt="" />
                                    ) : (
                                        <i className={`pi ${asset.mediaType === MEDIA_TYPE.VIDEO ? "pi-video" : "pi-image"}`} />
                                    )}
                                    <button
                                        type="button"
                                        className="article-section-editor__slideshow-item-remove"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteAsset(asset.uuid);
                                        }}
                                    >
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <MediaDropZone
                        accept="image/*,video/*"
                        multiple
                        disabled={uploading}
                        hint={t("knowledgeBase.form.dropSlideshowHint") as string}
                        onFilesSelected={(files) => handleFilesSelected(files)}
                    />
                    <Button
                        type="button"
                        label={t("knowledgeBase.form.selectExistingMedia") as string}
                        icon="pi pi-images"
                        outlined
                        className="article-section-editor__select-existing"
                        onClick={() => setPickerVisible(true)}
                    />
                    <SelectExistingMediaDialog
                        visible={pickerVisible}
                        onHide={() => setPickerVisible(false)}
                        candidates={existingMediaAssets.filter((candidate) => candidate.sectionUuid !== section.uuid)}
                        onSelect={handleSelectExisting}
                    />
                </div>
            );
        }

        // IMAGE or VIDEO — exactly one item, uploading (or picking) a new one replaces it (enforced server-side).
        const mediaType = section.sectionType === SECTION_TYPE.IMAGE ? MEDIA_TYPE.PHOTO : MEDIA_TYPE.VIDEO;
        const asset = assets.find((candidate) => candidate.mediaType === mediaType);
        const accept = section.sectionType === SECTION_TYPE.IMAGE ? "image/*" : "video/*";
        const hint =
            section.sectionType === SECTION_TYPE.IMAGE
                ? (t("knowledgeBase.form.dropImageHint") as string)
                : (t("knowledgeBase.form.dropVideoHint") as string);
        const isMatchingCandidate = (candidate: IMediaAssetModel) =>
            section.sectionType === SECTION_TYPE.IMAGE
                ? candidate.mediaType === MEDIA_TYPE.PHOTO || (candidate.mediaType === MEDIA_TYPE.FILE && isImageAttachment(candidate))
                : candidate.mediaType === MEDIA_TYPE.VIDEO || (candidate.mediaType === MEDIA_TYPE.FILE && isVideoAttachment(candidate));
        const singleItemCandidates = existingMediaAssets.filter(
            (candidate) => candidate.sectionUuid !== section.uuid && isMatchingCandidate(candidate)
        );

        return (
            <div className="article-section-editor__single-media">
                <MediaDropZone
                    accept={accept}
                    disabled={uploading}
                    hint={hint}
                    className="article-section-editor__media-drop"
                    onFilesSelected={(files) => handleFilesSelected(files, mediaType)}
                >
                    {asset && (
                        <div className="article-section-editor__media-preview">
                            {section.sectionType === SECTION_TYPE.VIDEO ? (
                                // Playing the video is not "choose another file": keep the click away from the drop zone.
                                <div onClick={(event) => event.stopPropagation()}>
                                    <DeferredVideoPlayer asset={asset} />
                                </div>
                            ) : (
                                photoDisplayUrl(asset) && <img src={photoDisplayUrl(asset)} alt="" />
                            )}
                            <span className="article-section-editor__media-replace">{t("knowledgeBase.form.replaceItem")}</span>
                        </div>
                    )}
                </MediaDropZone>
                <Button
                    type="button"
                    label={t("knowledgeBase.form.selectExistingMedia") as string}
                    icon="pi pi-images"
                    outlined
                    className="article-section-editor__select-existing"
                    onClick={() => setPickerVisible(true)}
                />
                <SelectExistingMediaDialog
                    visible={pickerVisible}
                    onHide={() => setPickerVisible(false)}
                    candidates={singleItemCandidates}
                    onSelect={handleSelectExisting}
                />
            </div>
        );
    };

    return (
        <div className="article-section-editor">
            <div className="article-section-editor__header">
                <i className={`pi ${SECTION_TYPE_ICON[section.sectionType]} article-section-editor__type-icon`} />
                <DashlyTextInput
                    className="article-section-editor__title-input"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={handleTitleBlur}
                />
                <div className="article-section-editor__header-actions">
                    <Button
                        type="button"
                        icon={showTitle ? "pi pi-eye" : "pi pi-eye-slash"}
                        text
                        rounded
                        title={t(showTitle ? "knowledgeBase.form.hideTitleOnPage" : "knowledgeBase.form.showTitleOnPage") as string}
                        onClick={() => onChange({ showTitle: !showTitle })}
                    />
                    <Button
                        type="button"
                        icon="pi pi-chevron-up"
                        text
                        rounded
                        disabled={isFirst}
                        title={t("knowledgeBase.form.moveSectionUp") as string}
                        onClick={() => onMove(MOVE_DIRECTION.UP)}
                    />
                    <Button
                        type="button"
                        icon="pi pi-chevron-down"
                        text
                        rounded
                        disabled={isLast}
                        title={t("knowledgeBase.form.moveSectionDown") as string}
                        onClick={() => onMove(MOVE_DIRECTION.DOWN)}
                    />
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        text
                        rounded
                        title={t("knowledgeBase.form.deleteSection") as string}
                        onClick={onDelete}
                    />
                </div>
            </div>
            <div className="article-section-editor__body">{renderBody()}</div>
            {pendingUpload && (
                <MediaUploadPreviewDialog files={pendingUpload.files} onConfirm={handlePreviewConfirm} onCancel={handlePreviewCancel} />
            )}
        </div>
    );
};

export default ArticleSectionEditor;
